import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import permissionService from '../services/permissionService';
import biometricAuthService from '../services/biometric/biometricAuthService';
import faceAuthService from '../services/biometric/faceAuthService';
import FaceBiometricModal from '../components/biometric/FaceBiometricModal';
import WorkspaceSelectionModal from '../components/auth/WorkspaceSelectionModal';
import './Login.css';

export const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  
  // Workspaces loaded dynamically based on username
  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [, setWorkspaceMessage] = useState('');
  
  const [formError, setFormError] = useState('');
  const [sessionRevokedNotice, setSessionRevokedNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [biometricInfo, setBiometricInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [tenantDomain, setTenantDomain] = useState(() => localStorage.getItem('lis_tenant_domain') || '');

  const handleTenantDomainChange = (e) => {
    const val = e.target.value;
    setTenantDomain(val);
    if (val) {
      localStorage.setItem('lis_tenant_domain', val);
      sessionStorage.setItem('lis_tenant_domain', val);
    } else {
      localStorage.removeItem('lis_tenant_domain');
      sessionStorage.removeItem('lis_tenant_domain');
    }
  };

  // Fallback modal if multi-workspace selection is requested by server
  const [pendingWorkspaces, setPendingWorkspaces] = useState([]);
  const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
  const [isFaceModalOpen, setIsFaceModalOpen] = useState(false);

  useEffect(() => {
    const revokedMsg = sessionStorage.getItem('lis_session_revoked_msg');
    if (revokedMsg) {
      setSessionRevokedNotice(revokedMsg);
      sessionStorage.removeItem('lis_session_revoked_msg');
    }
  }, []);

  const { login, loginWithPasskey, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';
  const debounceTimerRef = useRef(null);

  // Auto-detect tenant from username prefix if user typed it
  useEffect(() => {
    const u = username.trim().toLowerCase();
    let detectedDomain = null;
    if (u.startsWith('alpha_')) detectedDomain = 'alpha-lab.lims.local';
    else if (u.startsWith('beta_')) detectedDomain = 'beta-lab.lims.local';
    else if (u.startsWith('gamma_')) detectedDomain = 'gamma-lab.lims.local';
    
    if (detectedDomain && detectedDomain !== tenantDomain) {
      setTenantDomain(detectedDomain);
      localStorage.setItem('lis_tenant_domain', detectedDomain);
      sessionStorage.setItem('lis_tenant_domain', detectedDomain);
    }
  }, [username, tenantDomain]);

  // Debounced query of assigned workspaces when username is entered
  useEffect(() => {
    const trimmed = username.trim();
    if (!trimmed || trimmed.length < 2) {
      setUserWorkspaces([]);
      setSelectedCentreId('');
      setSelectedRoleId('');
      setWorkspaceMessage('');
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      setIsLoadingWorkspaces(true);
      try {
        const res = await permissionService.getWorkspacesByUsername(trimmed);
        if (res.success && Array.isArray(res.data) && res.data.length > 0) {
          const list = res.data;
          setUserWorkspaces(list);
          setWorkspaceMessage(`${list.length} workspace assignment(s) found`);

          // Select default centre or first centre
          const defaultWs = list.find((w) => w.isDefault) || list[0];
          setSelectedCentreId(defaultWs.centreId.toString());

          // Select role for that centre
          const rolesForDefaultCentre = list.filter((w) => w.centreId === defaultWs.centreId);
          const defaultRole = rolesForDefaultCentre.find((w) => w.isDefault) || rolesForDefaultCentre[0];
          setSelectedRoleId(defaultRole.roleId.toString());
        } else {
          setUserWorkspaces([]);
          setSelectedCentreId('');
          setSelectedRoleId('');
          setWorkspaceMessage('No pre-configured workspaces found. Will authenticate against primary account.');
        }
      } catch {
        setUserWorkspaces([]);
        setSelectedCentreId('');
        setSelectedRoleId('');
        setWorkspaceMessage('');
      } finally {
        setIsLoadingWorkspaces(false);
      }
    }, 250);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [username, tenantDomain]);

  // Extract unique centers from assigned workspaces
  const availableCentres = useMemo(() => {
    const map = new Map();
    userWorkspaces.forEach((w) => {
      if (!map.has(w.centreId)) {
        map.set(w.centreId, {
          centreId: w.centreId,
          centreName: w.centreName,
          centreCode: w.centreCode,
          isDefault: w.isDefault
        });
      }
    });
    return Array.from(map.values());
  }, [userWorkspaces]);

  // Extract roles assigned to the currently selected center
  const availableRolesForCentre = useMemo(() => {
    if (!selectedCentreId) return [];
    const centreIdNum = parseInt(selectedCentreId, 10);
    return userWorkspaces.filter((w) => w.centreId === centreIdNum);
  }, [userWorkspaces, selectedCentreId]);

  // When center changes, adjust role selection
  const handleCentreChange = (e) => {
    const newCentreId = e.target.value;
    setSelectedCentreId(newCentreId);

    const centreIdNum = parseInt(newCentreId, 10);
    const rolesForCentre = userWorkspaces.filter((w) => w.centreId === centreIdNum);
    if (rolesForCentre.length > 0) {
      const defRole = rolesForCentre.find((w) => w.isDefault) || rolesForCentre[0];
      setSelectedRoleId(defRole.roleId.toString());
    } else {
      setSelectedRoleId('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!username.trim()) {
      setFormError('Please enter your username / staff ID.');
      return;
    }

    if (!password) {
      setFormError('Please enter your password.');
      return;
    }

    setIsSubmitting(true);
    const centreIdToSend = selectedCentreId ? parseInt(selectedCentreId, 10) : null;
    const roleIdToSend = selectedRoleId ? parseInt(selectedRoleId, 10) : null;

    const result = await login(username.trim(), password, centreIdToSend, roleIdToSend);
    setIsSubmitting(false);

    if (result.success) {
      if (result.requiresWorkspaceSelection) {
        setPendingWorkspaces(result.workspaces || []);
        setIsWorkspaceModalOpen(true);
      } else {
        navigate(from, { replace: true });
      }
    } else {
      setFormError(result.error || 'Authentication failed. Please verify credentials.');
    }
  };

  const handleWorkspaceSelected = async (centreId, roleId) => {
    const res = await switchWorkspace(centreId, roleId);
    if (res.success) {
      setIsWorkspaceModalOpen(false);
      navigate(from, { replace: true });
    } else {
      throw new Error(res.message || 'Failed to select workspace.');
    }
  };

  // Check biometric/passkey availability on device
  useEffect(() => {
    biometricAuthService.checkBiometricAvailability().then(setBiometricInfo).catch(() => {});
  }, []);

  const handlePasskeyLogin = async () => {
    setFormError('');
    setIsPasskeyLoading(true);

    try {
      const centreIdToSend = selectedCentreId ? parseInt(selectedCentreId, 10) : null;
      const roleIdToSend = selectedRoleId ? parseInt(selectedRoleId, 10) : null;

      const resData = await biometricAuthService.loginWithPasskey({
        username: username.trim(),
        centreId: centreIdToSend,
        roleId: roleIdToSend,
      });

      const authResult = await loginWithPasskey(resData);
      if (authResult.success) {
        if (authResult.requiresWorkspaceSelection) {
          setPendingWorkspaces(authResult.workspaces || []);
          setIsWorkspaceModalOpen(true);
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setFormError(authResult.error || 'Passkey session creation failed.');
      }
    } catch (err) {
      console.error('Passkey login error:', err);
      setFormError(err.message || 'Authentication failed. Please try again or use your password.');
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const handleFaceLoginSuccess = async ({ faceEncoding }) => {
    setFormError('');
    const centreIdToSend = selectedCentreId ? parseInt(selectedCentreId, 10) : null;
    const roleIdToSend = selectedRoleId ? parseInt(selectedRoleId, 10) : null;

    const resData = await faceAuthService.loginWithFace({
      faceEncoding,
      username: username.trim(),
      centreId: centreIdToSend,
      roleId: roleIdToSend,
    });

    const authResult = await loginWithPasskey(resData);
    if (authResult.success) {
      setIsFaceModalOpen(false);
      if (authResult.requiresWorkspaceSelection) {
        setPendingWorkspaces(authResult.workspaces || []);
        setIsWorkspaceModalOpen(true);
      } else {
        navigate(from, { replace: true });
      }
    } else {
      throw new Error(authResult.error || 'Face login session creation failed.');
    }
  };

  return (
    <div className="lims-login-page-root">
      {/* ─── LEFT SIDE: HERO & LABORATORY BRANDING ─── */}
      <section className="lims-hero-pane" aria-label="Laboratory Information System Overview">
        <div className="lims-hero-ambient-glow" aria-hidden="true"></div>
        <div className="lims-hero-ambient-glow-2" aria-hidden="true"></div>
        <div className="lims-hero-grid-overlay" aria-hidden="true"></div>

        {/* Hero Header */}
        <header className="lims-hero-header">
          <div className="lims-brand-lockup">
            <div className="lims-brand-symbol">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                {/* Laboratory Flask + Medical Cross Symbol */}
                <path d="M10 2v6.5L4.5 18a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 8.5V2" />
                <path d="M8.5 2h7" />
                <path d="M12 11v4" />
                <path d="M10 13h4" />
              </svg>
            </div>
            <div className="lims-brand-title-wrap">
              <span className="lims-brand-name">inTeLiSYS</span>
              <span className="lims-brand-subtitle">Clinical Diagnostics Suite</span>
            </div>
          </div>

          <div className="lims-system-status-pill">
            <span className="lims-status-pulse-dot" aria-hidden="true"></span>
            <span>High Availability Active</span>
          </div>
        </header>

        {/* Hero Body Content */}
        <div className="lims-hero-body">
          <div className="lims-hero-eyebrow">
            <span>Next-Generation Diagnostic Technology</span>
          </div>

          <h1 className="lims-hero-headline">
            Precision in Every Sample.<br />
            <span className="lims-hero-headline-gradient">Confidence in Every Result.</span>
          </h1>

          <p className="lims-hero-subtext">
            Enterprise-grade Laboratory Information System integrating specimen accessioning,
            dual-stage pathologist sign-off, bi-directional analyzer interfacing, and multi-center RBAC.
          </p>

          {/* Feature Highlights Grid */}
          <div className="lims-hero-features-grid">
            <div className="lims-feature-card">
              <div className="lims-feature-icon">🔬</div>
              <div className="lims-feature-text">
                <h4>Specimen Workflow</h4>
                <p>Real-time barcoding, phlebotomy tracking, and specimen lifecycle integrity.</p>
              </div>
            </div>

            <div className="lims-feature-card">
              <div className="lims-feature-icon">🛡️</div>
              <div className="lims-feature-text">
                <h4>Multi-Center RBAC</h4>
                <p>Center-scoped permissions, explicit user overrides, and security audit logs.</p>
              </div>
            </div>

            <div className="lims-feature-card">
              <div className="lims-feature-icon">⚡</div>
              <div className="lims-feature-text">
                <h4>Digital Signatures</h4>
                <p>Dual-verification authorization with certified pathologist digital credentials.</p>
              </div>
            </div>

            <div className="lims-feature-card">
              <div className="lims-feature-icon">📊</div>
              <div className="lims-feature-text">
                <h4>NABL & ISO 15189</h4>
                <p>Standardized diagnostic templates, Delta check algorithms, and QC calibration.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Footer */}
        <footer className="lims-hero-footer">
          <span>&copy; {new Date().getFullYear()} inTeLiSYS. All rights reserved.</span>
          <div className="lims-hero-compliance-badges">
            <div className="lims-compliance-item" title="256-Bit TLS End-to-End Encryption">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>TLS 256-Bit</span>
            </div>
            <div className="lims-compliance-item" title="NABL / ISO 15189 Quality Compliant">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>ISO 15189 Ready</span>
            </div>
          </div>
        </footer>
      </section>

      {/* ─── RIGHT SIDE: AUTHENTICATION PANEL ─── */}
      <section className="lims-auth-pane" aria-label="Staff Authentication">
        <div className="lims-auth-card-container">
          <div className="lims-auth-card">
            {/* Top-Right Decorative Liquid Wave in Teal Theme */}
            <div className="lims-card-corner-wave" aria-hidden="true">
              <svg viewBox="0 0 160 110" fill="none" preserveAspectRatio="none">
                <path d="M0,0 L160,0 L160,110 C135,92 115,35 65,52 C28,64 14,18 0,0 Z" fill="url(#tealCornerGrad)" />
                <defs>
                  <linearGradient id="tealCornerGrad" x1="160" y1="0" x2="30" y2="90" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#14b8a6" stopOpacity="0.45" />
                    <stop offset="0.6" stopColor="#0d9488" stopOpacity="0.25" />
                    <stop offset="1" stopColor="#0f766e" stopOpacity="0.06" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Header: Flask Icon + Welcome Back */}
            <div className="lims-card-header">
              <div className="lims-card-flask-wrapper">
                <div className="lims-card-flask-glow" aria-hidden="true"></div>
                <svg className="lims-card-flask-icon" viewBox="0 0 48 48" fill="none">
                  <path d="M19 7h10M21 7v9.5L11.8 33.2A4 4 0 0 0 15.3 39h17.4a4 4 0 0 0 3.5-5.8L27 16.5V7" stroke="#0d9488" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M24 23v8M20 27h8" stroke="#0f766e" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="lims-card-header-text">
                <h2 className="lims-auth-title">Welcome Back</h2>
                <p className="lims-auth-subtitle">Sign in to continue to your authorized laboratory workspace.</p>
              </div>
            </div>

            {/* Form Alert Error */}
            {sessionRevokedNotice && (
              <div className="lims-auth-alert" style={{ background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid #f59e0b' }} role="alert" aria-live="assertive">
                <div style={{ color: '#d97706', display: 'flex', alignItems: 'center' }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{sessionRevokedNotice}</div>
              </div>
            )}

            {formError && (
              <div className="lims-auth-alert lims-auth-alert-error" role="alert" aria-live="assertive">
                <div className="lims-auth-alert-icon">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div>{formError}</div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="lims-auth-form" noValidate>
              {/* Tenant / Organization Selector */}
              <div className="lims-field-group">
                <div className="lims-field-label-row">
                  <label htmlFor="tenantDomain" className="lims-field-label">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                    <span>Client Tenant / Domain</span>
                  </label>
                </div>
                <div className="lims-input-wrapper">
                  <select
                    id="tenantDomain"
                    className="lims-input-control"
                    value={tenantDomain}
                    onChange={handleTenantDomainChange}
                    disabled={isSubmitting}
                    style={{ paddingLeft: '14px', appearance: 'auto' }}
                  >
                    <option value="">Primary Diagnostic Network (Default)</option>
                    <option value="alpha-lab.lims.local">Alpha Laboratory (alpha-lab.lims.local)</option>
                    <option value="beta-lab.lims.local">Beta Diagnostics (beta-lab.lims.local)</option>
                    <option value="gamma-lab.lims.local">Gamma Reference Labs (gamma-lab.lims.local)</option>
                  </select>
                </div>
              </div>

              {/* 1. Username */}
              <div className="lims-field-group">
                <div className="lims-field-label-row">
                  <label htmlFor="username" className="lims-field-label">
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>Username</span>
                  </label>
                  {isLoadingWorkspaces && (
                    <span className="lims-field-feedback feedback-loading">
                      <span className="lims-btn-spinner" style={{ width: 12, height: 12, borderWidth: 1.5, borderTopColor: '#0d9488' }}></span>
                      Loading centers...
                    </span>
                  )}
                </div>

                <div className="lims-input-wrapper">
                  <div className="lims-input-icon-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    type="text"
                    className="lims-input-control"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    disabled={isSubmitting}
                    autoFocus
                    required
                  />
                </div>
              </div>

              {/* 2. Password */}
              <div className="lims-field-group">
                <label htmlFor="password" className="lims-field-label">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <span>Password</span>
                </label>

                <div className="lims-input-wrapper">
                  <div className="lims-input-icon-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="lims-input-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    required
                  />
                  <button
                    type="button"
                    className="lims-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* 3. Center Name */}
              <div className="lims-field-group">
                <label htmlFor="centreSelect" className="lims-field-label">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18M4 18h16M6 18v-7M10 18v-7M14 18v-7M18 18v-7M2 11h20M12 3L2 11h20L12 3z" />
                  </svg>
                  <span>Center Name</span>
                </label>

                <div className={`lims-input-wrapper ${availableCentres.length === 0 ? 'disabled' : ''}`}>
                  <div className="lims-input-icon-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21h18M4 18h16M6 18v-7M10 18v-7M14 18v-7M18 18v-7M2 11h20M12 3L2 11h20L12 3z" />
                    </svg>
                  </div>
                  <select
                    id="centreSelect"
                    className="lims-input-control lims-select-control"
                    value={selectedCentreId}
                    onChange={handleCentreChange}
                    disabled={isSubmitting || availableCentres.length === 0}
                  >
                    {availableCentres.length === 0 ? (
                      <option value="">
                        {username.trim().length >= 2 ? 'Select Center' : 'Enter username first'}
                      </option>
                    ) : (
                      availableCentres.map((c) => (
                        <option key={c.centreId} value={c.centreId}>
                          {c.centreName}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* 4. Role */}
              <div className="lims-field-group">
                <label htmlFor="roleSelect" className="lims-field-label">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <span>Role</span>
                </label>

                <div className={`lims-input-wrapper ${availableRolesForCentre.length === 0 ? 'disabled' : ''}`}>
                  <div className="lims-input-icon-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  </div>
                  <select
                    id="roleSelect"
                    className="lims-input-control lims-select-control"
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    disabled={isSubmitting || availableRolesForCentre.length === 0}
                  >
                    {availableRolesForCentre.length === 0 ? (
                      <option value="">
                        {availableCentres.length > 0 ? 'Select Role' : 'Enter username first'}
                      </option>
                    ) : (
                      availableRolesForCentre.map((r) => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.roleName ? (r.roleName.charAt(0).toUpperCase() + r.roleName.slice(1).toLowerCase()) : ''}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="lims-submit-btn"
                disabled={isSubmitting || isPasskeyLoading}
                id="btn-login-submit"
              >
                {isSubmitting ? (
                  <>
                    <span className="lims-btn-spinner" aria-hidden="true"></span>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <span className="lims-submit-arrow">&rarr;</span>
                  </>
                )}
              </button>

              {/* Passkey / Biometric Login Option */}
              <div className="lims-login-divider">
                <span>OR</span>
              </div>

              <div className="lims-biometric-group">
                <button
                  type="button"
                  className="lims-face-btn"
                  onClick={() => {
                    setFormError('');
                    setIsFaceModalOpen(true);
                  }}
                  disabled={isSubmitting || isPasskeyLoading}
                  id="btn-login-face"
                  title="Sign in with Facial Recognition"
                  aria-label="Login with Facial Recognition"
                >
                  <svg
                    className="lims-passkey-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="9" cy="10" r="1.2" fill="currentColor" />
                    <circle cx="15" cy="10" r="1.2" fill="currentColor" />
                    <path d="M8 15s1.5 2 4 2 4-2 4-2" />
                  </svg>
                  <span>Login with Face</span>
                </button>

                <button
                  type="button"
                  className="lims-passkey-btn"
                  onClick={handlePasskeyLogin}
                  disabled={isSubmitting || isPasskeyLoading}
                  id="btn-login-passkey"
                  title={biometricInfo?.providerName ? `Sign in with ${biometricInfo.providerName}` : 'Sign in with Passkey / Fingerprint'}
                  aria-label="Login with Fingerprint or Passkey"
                >
                  {isPasskeyLoading ? (
                    <>
                      <span className="lims-btn-spinner lims-passkey-spinner" aria-hidden="true"></span>
                      <span>Verifying Biometric / Passkey...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="lims-passkey-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 004.07 9m5.93 11a14.015 14.015 0 005.17-5.17" />
                      </svg>
                      <span>Login with Fingerprint / Passkey</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Footer Security Notice */}
          <footer className="lims-auth-footer">
            <div className="lims-auth-security-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Authorized Healthcare Personnel Only &bull; Encrypted Session</span>
            </div>
            <span className="lims-auth-version-text">Enterprise Diagnostic Build v2.4.0-rbac</span>
          </footer>
        </div>

        {/* Fallback Workspace Selection Modal */}
        <WorkspaceSelectionModal
          isOpen={isWorkspaceModalOpen}
          workspaces={pendingWorkspaces}
          onSelect={handleWorkspaceSelected}
          onClose={() => setIsWorkspaceModalOpen(false)}
          title="Select Operating Workspace"
          subtitle="Your account is authorized for multiple diagnostic centers or roles. Choose your active workspace to continue."
        />

        {/* Biometric Face Login Modal */}
        <FaceBiometricModal
          isOpen={isFaceModalOpen}
          onClose={() => setIsFaceModalOpen(false)}
          mode="login"
          onSuccess={handleFaceLoginSuccess}
          onFallback={() => setIsFaceModalOpen(false)}
        />
      </section>
    </div>
  );
};

export default Login;
